import React from 'react';
import { observer } from 'mobx-react-lite';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import SectionTitle from 'components/common/general/SectionTitle';
import {
  ProposalExperience,
  ProposalExperienceAccessLimitations,
  ProposalExperienceDisabledReasons,
} from 'store/modules/initiate-quote/types';
import { RenewalActionsDelegate } from 'store/modules/initiate-quote/renewalActionsDelegate';
import {
  CUSTOM_PROPOSAL_RADIO_OPTION_TEST_ID,
  FAVORITE_QUOTE_RADIO_OPTION_TEST_ID,
  INITIATE_PROPOSAL_EXPERIENCE_FORM_ID,
  RENEWAL_CUSTOM_QUOTE_RADIO_OPTION_TEST_ID,
  RENEWAL_SHELF_QUOTE_RADIO_OPTION_TEST_ID,
  SHELF_QUOTE_RADIO_OPTION_TEST_ID,
} from '../constants';
import { IMRadioGroupStyled } from './styles';
import QuoteExperienceOption from './QuoteExperienceOption';
import { TabIMFormStyled } from '../styles';
import RenewalActions from '../RenewalActions';

const EXPERIENCE_RADIO_CONFIG: Record<ProposalExperience, { id: string; key: string; 'data-test-id': string }> = {
  SHELF: {
    id: 'shelf-proposal',
    key: 'shelf-proposal',
    'data-test-id': SHELF_QUOTE_RADIO_OPTION_TEST_ID,
  },
  FAVORITE: {
    id: 'favorite-proposal',
    key: 'favorite-proposal',
    'data-test-id': FAVORITE_QUOTE_RADIO_OPTION_TEST_ID,
  },
  CUSTOM: {
    id: 'custom-proposal',
    key: 'custom-proposal',
    'data-test-id': CUSTOM_PROPOSAL_RADIO_OPTION_TEST_ID,
  },
  RENEWAL_SHELF: {
    id: 'renewal-shelf-proposal',
    key: 'renewal-shelf-quote',
    'data-test-id': RENEWAL_SHELF_QUOTE_RADIO_OPTION_TEST_ID,
  },
  RENEWAL_CUSTOM: {
    id: 'renewal-custom-proposal',
    key: 'renewal-custom-proposal',
    'data-test-id': RENEWAL_CUSTOM_QUOTE_RADIO_OPTION_TEST_ID,
  },
};

interface Props {
  formController: FormController<{
    proposalExperience: FormControl<string>;
  }>;
  experiences: ProposalExperience[];
  formId?: string;
  experienceDisabledReasons?: ProposalExperienceDisabledReasons;
  experienceAccessLimitations?: ProposalExperienceAccessLimitations;
  renewalActionsDelegate?: RenewalActionsDelegate;
}

const QuoteExperienceStep: React.FC<Props> = observer(
  ({
    formController,
    experiences,
    formId = INITIATE_PROPOSAL_EXPERIENCE_FORM_ID,
    experienceDisabledReasons,
    experienceAccessLimitations,
    renewalActionsDelegate,
  }) => {
    const radioButtons = experiences
      .map((experience) => {
        const disabledReason = experienceDisabledReasons?.[experience] ?? null;
        const accessLimitation = experienceAccessLimitations?.[experience];

        return {
          ...EXPERIENCE_RADIO_CONFIG[experience],
          value: experience,
          disabled: !!disabledReason,
          label: (
            <QuoteExperienceOption
              experience={experience}
              disabledReason={disabledReason}
              accessLimitation={accessLimitation}
            >
              {experience === 'RENEWAL_SHELF' && renewalActionsDelegate && (
                <RenewalActions renewalActionsDelegate={renewalActionsDelegate} />
              )}
            </QuoteExperienceOption>
          ),
        };
      })
      .sort((a, b) => Number(a.disabled) - Number(b.disabled));

    return (
      <TabIMFormStyled id={formId} formController={formController}>
        <SectionTitle title="Proposal Experience" subtitle="Select the proposal experience that best fits your needs" />
        <IMRadioGroupStyled name="proposalExperience" size="full" radioButtons={radioButtons} />
      </TabIMFormStyled>
    );
  },
);

export default QuoteExperienceStep;
